import { useEffect, useRef, useState } from "react";
import { Check, Clock, Link2, Loader2, Minus, Play, Plus } from "lucide-react";
import { Link } from "react-router-dom";

import {
  isPermissionExpired,
  permissionDaysRemaining,
  shouldShowDaysRemaining,
} from "@/hooks/usePermissions";
import { useAppStore } from "@/lib/store";
import type { CoursePermissionWithCourse } from "@/lib/schema";

interface DashboardCourseCardProps {
  permission: CoursePermissionWithCourse;
  instructorImage: string;
  onShare: (courseId: string) => Promise<boolean>;
  onExtend: (permissionId: string) => void;
  /** Whether the extend/renew action is currently in progress for this card. */
  extending?: boolean;
  performanceHref: string;
  progressPercent?: number;
  onRemove?: () => void;
  /** When rendered inside a performance, pass the playlist ID so the course page can show comments. */
  playlistId?: string | null;
  /** Sequence number within a performance (1-based). */
  sequenceNumber?: number;
  /** Organizer notes for this course within the performance. */
  notes?: string | null;
  /** Callback when the organizer saves a note. Returns an error message on failure. */
  onNoteSave?: (notes: string | null) => Promise<string | null>;
}

export function DashboardCourseCard({
  permission,
  instructorImage,
  onShare,
  onExtend,
  extending = false,
  performanceHref,
  progressPercent,
  onRemove,
  playlistId,
  sequenceNumber,
  notes,
  onNoteSave,
}: DashboardCourseCardProps) {
  const [copied, setCopied] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [noteText, setNoteText] = useState(notes ?? "");
  const noteRef = useRef<HTMLTextAreaElement>(null);

  // Sync local noteText when the notes prop changes (e.g. after optimistic update or refetch)
  useEffect(() => {
    if (!isEditingNote) {
      setNoteText(notes ?? "");
      setNoteError(null);
    }
  }, [notes, isEditingNote]);
  const { addToCart, openModal, setCartOpen, setSelectedAddToPlaylistCourseId } = useAppStore();

  const courseHref = `/app/dashboard/course?course_id=${permission.course_id.id}${playlistId ? `&playlist_id=${playlistId}` : ""}`;
  const instructorName = permission.course_id.Instructor?.name ?? "Khusbu Gemmati";
  const expired = isPermissionExpired(permission);
  const days = permissionDaysRemaining(permission);
  const showDays = shouldShowDaysRemaining(permission);
  const handleShare = async () => {
    const didCopy = await onShare(permission.course_id.id);
    if (!didCopy) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const formattedCoursePrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Number.isInteger(permission.course_id.price) ? 0 : 2,
  }).format(permission.course_id.price);

  const handleBuyAgain = () => {
    addToCart({
      type: "course",
      course_id: permission.course_id.id,
      name: permission.course_id.name,
      price: permission.course_id.price,
    });
    setCartOpen(true);
  };

  const focusNoteField = () => {
    window.setTimeout(() => noteRef.current?.focus(), 0);
  };

  const openNoteEditor = () => {
    setNoteError(null);
    setIsEditingNote(true);
    focusNoteField();
  };

  const handleSaveNote = async () => {
    if (!onNoteSave) return;
    const trimmed = noteText.trim();
    setIsSavingNote(true);
    setNoteError(null);
    const error = await onNoteSave(trimmed || null);
    setIsSavingNote(false);
    if (error) {
      setNoteError(error);
      return;
    }
    setIsEditingNote(false);
  };

  const handleRemoveNote = async () => {
    if (!onNoteSave) return;
    setIsSavingNote(true);
    setNoteError(null);
    const error = await onNoteSave(null);
    setIsSavingNote(false);
    if (error) {
      setNoteError(error);
      return;
    }
    setNoteText("");
    setIsEditingNote(false);
  };

  return (
    <article
      className="relative flex h-full w-full max-w-[38rem] flex-col overflow-visible rounded-[1.2rem] border border-[#dfdbde] bg-[#fafafa] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)] max-[479px]:rounded-[1rem]"
      data-testid={`dashboard-owned-${permission.course_id.id}`}
    >
      {sequenceNumber != null && (
        <div className="absolute -left-3 top-0 z-10 -translate-y-1/2 flex h-[1.85rem] w-[1.85rem] items-center justify-center rounded-full bg-[#f3e8ef] text-[0.82rem] font-bold text-[#762a5c] shadow-[0_0_0_4px_#f6f5f6]">
          {sequenceNumber}
        </div>
      )}
      <Link to={courseHref} className="relative block m-[0.9rem] aspect-[1.75] overflow-hidden rounded-[1rem] bg-[#ece7ea] max-[479px]:m-[0.75rem] max-[479px]:rounded-[0.85rem]">
        {permission.course_id.thumbnail_links_course ? (
          <img
            src={permission.course_id.thumbnail_links_course}
            alt={permission.course_id.name}
            className="w-full h-full object-cover block"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full object-cover block bg-[linear-gradient(135deg,#ffb239_0%,#f4a232_45%,#f0ecef_45%,#f0ecef_100%)]" />
        )}

        <div className="absolute left-1/2 top-1/2 inline-flex h-[3.5rem] w-[3.5rem] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/[0.38] text-white backdrop-blur-[8px] max-[479px]:h-[3rem] max-[479px]:w-[3rem]">
          <Play className="h-5 w-5 max-[479px]:h-4 max-[479px]:w-4" />
        </div>
      </Link>

      <div className="flex items-center justify-between gap-4 px-[0.95rem] pb-[0.95rem] pt-[0.1rem] max-[479px]:gap-3 max-[479px]:px-[0.75rem] max-[479px]:pb-[0.85rem]">
        <div className="flex min-w-0 items-center gap-[0.65rem]">
          <div className="h-[1.65rem] w-[1.65rem] flex-none overflow-hidden rounded-[0.3rem]">
            <img
              src={instructorImage}
              alt={instructorName}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>

          <div className="min-w-0">
            <div className="text-[#b0a8ad] text-[0.58rem] font-bold tracking-[0.25em] uppercase">Instructor</div>
            <div className="text-[#3a3338] text-[0.82rem] font-bold leading-[1.15]">{instructorName}</div>
          </div>
        </div>

        <div className="inline-flex shrink-0 items-center gap-[0.55rem]">
          <button
            type="button"
            className={`inline-flex h-8 w-8 items-center justify-center rounded-full border ${
              expired
                ? "cursor-not-allowed border-[#eee9ec] bg-[#f5f2f4] text-[#c2bac0]"
                : "border-[#e1dde0] bg-[#fafafa] text-[#7c7479]"
            }`}
            aria-label={`Copy share link for ${permission.course_id.name}`}
            disabled={expired}
            onClick={() => {
              if (expired) return;
              void handleShare();
            }}
          >
            {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
          </button>

          {onRemove ? (
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center border border-[#e1dde0] rounded-full bg-[#fafafa] text-[#7c7479]"
              aria-label={`Remove ${permission.course_id.name} from performance`}
              onClick={onRemove}
            >
              <Minus className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center border border-[#e1dde0] rounded-full bg-[#fafafa] text-[#7c7479]"
              aria-label={`Add ${permission.course_id.name} to a performance`}
              onClick={() => {
                setSelectedAddToPlaylistCourseId(permission.course_id.id);
                openModal("save-to-performance");
              }}
            >
              <Plus className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="border-t border-[#ebe7ea] px-[0.95rem] pb-[1.25rem] pt-[1.1rem] max-[479px]:px-[0.75rem] max-[479px]:pb-[1rem] max-[479px]:pt-[0.95rem]">
        <Link to={courseHref} className="inline-block font-[var(--font-display)] text-[1.15rem] leading-[1.2] text-[#321b29] max-[479px]:text-[1.02rem]">
          {permission.course_id.name}
        </Link>

        {progressPercent != null && progressPercent > 0 && (
          <div className="mt-[0.85rem] flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#f0edef]">
              <div
                className="h-full rounded-full bg-[#762a5c] transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-[0.78rem] font-medium text-[#7f777d]">{progressPercent}%</span>
          </div>
        )}

        {showDays && (
          <div className="mt-[0.85rem]">
            {expired ? (
              <div className="flex flex-col gap-[0.65rem]">
                <div className="flex items-center gap-[0.4rem] text-[0.88rem] font-medium text-[#ba4d4d]">
                  <Clock className="h-[0.9rem] w-[0.9rem]" />
                  <span>Your access has expired</span>
                </div>
                <button
                  type="button"
                  className="inline-flex min-h-[2.75rem] w-full cursor-pointer items-center justify-center gap-[0.45rem] rounded-[0.9rem] border border-[#8b2965] bg-[#8b2965] px-[1.2rem] text-center text-[0.93rem] leading-tight font-semibold text-white transition-opacity hover:opacity-90 max-[479px]:px-[0.95rem] max-[479px]:text-[0.89rem]"
                  aria-label={`Buy ${permission.course_id.name} again for ${formattedCoursePrice}`}
                  onClick={handleBuyAgain}
                >
                  Buy Again for {formattedCoursePrice}
                </button>
              </div>
            ) : days !== Infinity ? (
              <div className="flex flex-col gap-[0.65rem]">
                <div className="flex items-center gap-[0.4rem] text-[0.88rem] font-medium text-[#6f3f5d]">
                  <Clock className="h-[0.9rem] w-[0.9rem]" />
                  <span>Days Remaining: {days}</span>
                </div>
                <button
                  type="button"
                  className="inline-flex min-h-[2.75rem] w-full cursor-pointer items-center justify-center gap-[0.45rem] rounded-[0.9rem] border border-[#ddd9dc] bg-white px-[1.2rem] text-center text-[0.93rem] leading-tight font-semibold text-[#2a2227] transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-70 max-[479px]:px-[0.95rem] max-[479px]:text-[0.89rem]"
                  aria-label={`Extend access for ${permission.course_id.name} at $10`}
                  disabled={extending}
                  onClick={() => onExtend(permission.id)}
                >
                  {extending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Redirecting to checkout…
                    </>
                  ) : (
                    <>Extend for $10</>
                  )}
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Organizer notes */}
      {onNoteSave && (
        <div className="flex flex-1 flex-col border-t border-[#ebe7ea] px-[0.95rem] pb-[1.1rem] pt-[0.85rem] max-[479px]:px-[0.75rem]">
          {isEditingNote ? (
            <div>
              <textarea
                ref={noteRef}
                className="w-full resize-none rounded-[0.5rem] border border-[#e2e4e9] bg-white px-3 py-2 font-[family-name:Inter,sans-serif] text-[0.85rem] leading-[1.45] text-[#321b29] placeholder:text-[#b0a8ad] focus:border-[#762a5c] focus:outline-none disabled:opacity-70"
                rows={3}
                placeholder="Add a note for this tutorial..."
                value={noteText}
                disabled={isSavingNote}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-[0.4rem] border border-[#762a5c] bg-[#762a5c] px-3 py-1.5 font-[family-name:Inter,sans-serif] text-[0.8rem] font-medium leading-none text-white transition-transform duration-200 hover:-translate-y-[3%] active:scale-95 disabled:opacity-60"
                  disabled={isSavingNote}
                  onClick={() => void handleSaveNote()}
                >
                  {isSavingNote ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-[0.4rem] border border-[#e2e4e9] bg-white px-3 py-1.5 font-[family-name:Inter,sans-serif] text-[0.8rem] font-medium leading-none text-[#525866] transition-transform duration-200 hover:-translate-y-[3%] active:scale-95 disabled:opacity-60"
                  disabled={isSavingNote}
                  onClick={() => {
                    setNoteText(notes ?? "");
                    setNoteError(null);
                    setIsEditingNote(false);
                  }}
                >
                  Cancel
                </button>
                {notes && (
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center justify-center rounded-[0.4rem] border border-[#e2e4e9] bg-white px-3 py-1.5 font-[family-name:Inter,sans-serif] text-[0.8rem] font-medium leading-none text-[#ba4d4d] transition-transform duration-200 hover:-translate-y-[3%] active:scale-95 disabled:opacity-60"
                    disabled={isSavingNote}
                    onClick={() => void handleRemoveNote()}
                  >
                    {isSavingNote ? "Removing..." : "Remove"}
                  </button>
                )}
              </div>
              {noteError && (
                <p className="mt-2 m-0 text-[0.78rem] font-medium text-[#ba4d4d]">
                  {noteError}
                </p>
              )}
            </div>
          ) : notes ? (
            <div className="flex flex-1 flex-col">
              <div className="mb-1 text-[0.6rem] font-bold uppercase tracking-[0.2em] text-[#b0a8ad]">
                Note
              </div>
              <p className="m-0 whitespace-pre-wrap text-[0.85rem] leading-[1.45] text-[#4a3f47]">
                {notes}
              </p>
              <button
                type="button"
                className="mt-auto self-start pt-2 text-left text-[0.78rem] font-medium text-[#762a5c] hover:underline"
                onClick={openNoteEditor}
              >
                Edit note
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="flex items-center gap-1.5 text-[0.8rem] font-medium text-[#7f777d] hover:text-[#762a5c]"
              onClick={openNoteEditor}
            >
              <svg width="14" height="14" viewBox="0 0 256 256" fill="currentColor">
                <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm48-88a8,8,0,0,1-8,8H136v32a8,8,0,0,1-16,0V136H88a8,8,0,0,1,0-16h32V88a8,8,0,0,1,16,0v32h32A8,8,0,0,1,176,128Z" />
              </svg>
              Add note
            </button>
          )}
        </div>
      )}
    </article>
  );
}
